"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FreeGiveawayPromotion = void 0;
const typings_1 = require("../../typings");
const utils_1 = require("../../utils");
const base_1 = require("../base");
class FreeGiveawayPromotion extends base_1.BasePromotion {
    constructor(props) {
        super(props);
        this.props = props;
    }
    /**
     * Get free products
     * @returns {ReadonlyArray<FreeProduct>}
     */
    get freeProducts() {
        return this.props.freeProducts;
    }
    /**
     * Get award from total price (including additional discount) or award from sum of product skus flag
     * @returns {boolean}
     */
    get isAwardFromTotalPrice() {
        return this.props.settings.awardTotalPrice;
    }
    /**
     * Giveaway free products by applying
     * free product in cart
     *
     * Remark: This will mutate productSKUs in Cart object
     * @param cart
     */
    giveawayFreeProducts(cart, matchedCriteriaFreeProducts) {
        const cartWithFreeProducts = Object.assign({}, cart);
        let freeProducts = [];
        let promotionRemarks = [];
        matchedCriteriaFreeProducts.forEach((freeProduct) => {
            const stockAmountAfterGiveaway = freeProduct.stock - freeProduct.amount;
            /**
             * System has enough to giveaway, will giveaway with
             * desire amount and proceed to next iteration
             */
            if (stockAmountAfterGiveaway > 0) {
                freeProducts = freeProducts.concat([
                    Object.assign(Object.assign({}, freeProduct), { amount: freeProduct.amount }),
                ]);
                // Remark in promotion on ful amount that system fulfills
                promotionRemarks = promotionRemarks.concat([
                    {
                        fulfilledAmount: freeProduct.amount,
                        productSKUId: freeProduct.id,
                        desireAmount: freeProduct.amount,
                        missingAmount: 0,
                    },
                ]);
                return;
            }
            /**
             * Stock does not have enough to giveaway,
             * system will give out as much as it has.
             *
             * e.g. stock: 3, giveaway amount: 4
             * system will give out 3 and remark in promotion that
             * the system can only give 3 at maximum
             */
            const fulfilledAmount = stockAmountAfterGiveaway + freeProduct.amount;
            promotionRemarks = promotionRemarks.concat([
                {
                    fulfilledAmount,
                    productSKUId: freeProduct.id,
                    desireAmount: freeProduct.amount,
                    missingAmount: freeProduct.amount - fulfilledAmount,
                },
            ]);
            /**
             * If fulfillable amount is more than 0 meaning that
             * system can giveaway still even if it is less than
             * desire amount, system then push giveaway item to
             * user's cart
             *
             * Remark: This will prevent system from pushing giveaway item
             * into user's cart without actually give anything to user
             */
            if (fulfilledAmount > 0) {
                freeProducts = freeProducts.concat([Object.assign(Object.assign({}, freeProduct), { amount: fulfilledAmount })]);
            }
        });
        const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(cartWithFreeProducts.productSKUs, { remarks: promotionRemarks });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, cartWithFreeProducts), { productSKUs: productSKUsWithAppliedPromotion.concat(freeProducts) }), { remarks: promotionRemarks });
    }
    /**
     * Apply free giveaway for any product
     * e.g. Buy any product to get free giveaway
     * @param cart
     */
    applyFreeGiveawayForAnyProductPromotion(cart) {
        if (!cart.productSKUs.length) {
            return cart;
        }
        const [firstCriteria] = this.criteria;
        if (!firstCriteria) {
            return cart;
        }
        const freeProducts = firstCriteria.freeProducts || [];
        const hasFreeProducts = (0, utils_1.hasSomeFreeProductsToGiveaway)(freeProducts);
        if (!hasFreeProducts) {
            return cart;
        }
        // Giveaway free products
        const cartWithFreeProducts = this.giveawayFreeProducts(cart, freeProducts);
        return cartWithFreeProducts;
    }
    /**
     * Apply free giveaway for any selected product
     * e.g. Buy any selected product to get free giveaway
     * @param cart
     */
    applyFreeGiveawayForAnySelectedProductPromotion(cart) {
        if (!cart.productSKUs.length) {
            return cart;
        }
        const [firstCriteria] = this.criteria;
        if (!firstCriteria) {
            return cart;
        }
        const freeProducts = firstCriteria.freeProducts || [];
        const hasFreeProducts = (0, utils_1.hasSomeFreeProductsToGiveaway)(freeProducts);
        if (!hasFreeProducts) {
            return cart;
        }
        const hasSelectedProducts = (0, utils_1.hasSomeSelectedProductsInProductSKUs)(cart.productSKUs, this.selectedProductIds);
        if (!hasSelectedProducts) {
            return cart;
        }
        // Giveaway free products
        const cartWithFreeProducts = this.giveawayFreeProducts(cart, freeProducts);
        return cartWithFreeProducts;
    }
    /**
     * Apply free giveaway for any product in total of X price
     * e.g. 200B of any product to get free-giveaway
     * @param cart
     */
    applyFreeGiveawayForInTotalOfAnyProductInBundlePromotion(cart) {
        if (!cart.productSKUs.length) {
            return cart;
        }
        /**
         * Award with total price which includes additional discount to compare
         * against the price threshold
         * e.g.
         * 290 with threshold of 500B
         *
         * 500 (threshold) > 290 (total)
         */
        const matchedCriteria = this.isAwardFromTotalPrice
            ? this.getCriteriaByTarget(cart.totalPrice)
            : this.getCriteriaByTarget((0, utils_1.getTotalPrice)(cart.productSKUs));
        if (!matchedCriteria) {
            return cart;
        }
        const freeProducts = matchedCriteria.freeProducts || [];
        const hasFreeProducts = (0, utils_1.hasSomeFreeProductsToGiveaway)(freeProducts);
        if (!hasFreeProducts) {
            return cart;
        }
        // Giveaway free products
        const cartWithFreeProducts = this.giveawayFreeProducts(cart, freeProducts);
        return cartWithFreeProducts;
    }
    /**
     * Apply promotion
     * @param cart
     * @returns {Cart}
     */
    applyPromotion(cart) {
        switch (this.settingCondition) {
            case typings_1.PromotionCondition.BUY_ANY_PRODUCT:
                return this.applyFreeGiveawayForAnyProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_ANY_SELECTED_PRODUCT:
                return this.applyFreeGiveawayForAnySelectedProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PRICE_IN_TOTAL_OF_ANY_PRODUCT:
                return this.applyFreeGiveawayForInTotalOfAnyProductInBundlePromotion(cart);
            default:
                return cart;
        }
    }
}
exports.FreeGiveawayPromotion = FreeGiveawayPromotion;
//# sourceMappingURL=free-giveaway.js.map